import { handleApiCall } from "../apis";
import { endpoint } from "./endpoints";

function responseBody(response) {
  if (response?.isAxiosError) return response.response?.data ?? {};
  return response?.data ?? response ?? {};
}

function isApiOk(body) {
  if (!body || typeof body !== "object") return false;
  if (body.code != null) return Number(body.code) === 200 || Number(body.code) === 201;
  return body.success === true;
}

function apiMessage(body, fallback) {
  return String(
    body?.message || body?.errorMessage || body?.error || fallback || ""
  ).trim();
}

/** `reason` values for POST .../suborder/reject */
export const SHOP_SUBORDER_REJECT_REASONS = [
  { value: "out_of_stock", label: "Out of stock" },
  { value: "cannot_deliver", label: "Cannot deliver to location" },
  { value: "pricing_error", label: "Pricing error" },
  { value: "other", label: "Other" },
];

function resolveVendorSuborder(row) {
  if (!row || typeof row !== "object") return null;
  if (row.suborder && typeof row.suborder === "object") return row.suborder;
  if (Array.isArray(row.vendor_suborders) && row.vendor_suborders[0]) {
    return row.vendor_suborders[0];
  }
  const vf = row.vendor_fulfillment;
  if (Array.isArray(vf) && vf[0]) return vf[0];
  if (vf && typeof vf === "object" && vf.vendor_id) return vf;
  return null;
}

function unwrapReturnRequestDocument(raw) {
  if (!raw || typeof raw !== "object") return null;
  if (raw._doc && typeof raw._doc === "object") {
    const doc = { ...raw._doc };
    if (Array.isArray(raw.evidence_images) && raw.evidence_images.length) {
      doc.evidence_images = raw.evidence_images;
    }
    if (Array.isArray(raw.evidence_videos) && raw.evidence_videos.length) {
      doc.evidence_videos = raw.evidence_videos;
    }
    return doc;
  }
  if (raw.status != null || raw.reason != null || raw.customer_note != null) {
    return raw;
  }
  return null;
}

function resolveEvidenceUrl(entry) {
  if (!entry) return "";
  if (typeof entry === "string") {
    const s = entry.trim();
    if (!s) return "";
    if (/^https?:\/\//i.test(s)) return s;
    return `https://eventmanagements3bucket.s3.ap-south-1.amazonaws.com/${s.replace(/^\//, "")}`;
  }
  if (typeof entry === "object") {
    const url = String(entry.url ?? entry.secure_url ?? "").trim();
    if (url) return url;
    const key = String(entry.key ?? entry.path ?? "").trim();
    if (key) return resolveEvidenceUrl(key);
  }
  return "";
}

function mapReturnEvidence(images, videos) {
  const imageList = (Array.isArray(images) ? images : [])
    .map((item) => {
      const url = resolveEvidenceUrl(item);
      return url ? { url } : null;
    })
    .filter(Boolean);
  const videoList = (Array.isArray(videos) ? videos : [])
    .map((item) => {
      const url = resolveEvidenceUrl(item);
      return url ? { url } : null;
    })
    .filter(Boolean);
  return { images: imageList, videos: videoList };
}

function collectReturnRequestCandidates(row, sub) {
  const candidates = [
    sub?.return_request,
    row?.return_request,
    row?.suborder?.return_request,
    row?.vendor_fulfillment?.return_request,
  ];
  if (Array.isArray(row?.vendor_fulfillment)) {
    row.vendor_fulfillment.forEach((vf) => {
      if (vf?.return_request) candidates.push(vf.return_request);
    });
  }
  if (Array.isArray(row?.vendor_suborders)) {
    row.vendor_suborders.forEach((s) => {
      if (s?.return_request) candidates.push(s.return_request);
    });
  }
  return candidates;
}

function inferReturnFromTimeline(row, sub) {
  const pools = [
    row?.timeline,
    sub?.timeline,
    row?.vendor_fulfillment?.timeline,
    row?.suborder?.timeline,
  ];
  const steps = pools.flat().filter((s) => s && typeof s === "object");
  const requested = [...steps]
    .reverse()
    .find((s) => normalizeStatusKey(s.status) === "return_requested");
  const rejected = [...steps]
    .reverse()
    .find((s) => normalizeStatusKey(s.status) === "return_rejected");

  const orderKey = normalizeStatusKey(
    sub?.order_status || row?.vendor_order_status || row?.order_status
  );
  if (!requested && !rejected && !orderKey.includes("return")) return null;

  let status = "requested";
  if (rejected || orderKey === "return_rejected") status = "rejected";
  else if (orderKey === "return_approved") status = "approved";

  return {
    status,
    reason: String(requested?.description || requested?.title || "").trim(),
    customerNote: "",
    vendorNote: String(rejected?.description || sub?.status_note || "").trim(),
    decidedBy: "",
    requestedAt: requested?.at || "",
    resolvedAt: rejected?.at || "",
    evidenceImages: [],
    evidenceVideos: [],
  };
}

export function resolveVendorReturnRequest(row) {
  if (!row || typeof row !== "object") return null;
  const sub = resolveVendorSuborder(row) || row;

  for (const c of collectReturnRequestCandidates(row, sub)) {
    const doc = unwrapReturnRequestDocument(c);
    if (!doc) continue;
    const status = String(doc.status ?? doc.return_status ?? "").trim().toLowerCase();
    if (!status || status === "none") continue;
    const evidence = mapReturnEvidence(doc.evidence_images, doc.evidence_videos);
    return {
      status,
      reason: String(doc.reason || doc.return_reason || "").trim(),
      customerNote: String(doc.customer_note || "").trim(),
      vendorNote: String(doc.vendor_note || "").trim(),
      decidedBy: String(doc.decided_by || "").trim(),
      requestedAt: doc.requested_at || "",
      resolvedAt: doc.resolved_at || "",
      evidenceImages: evidence.images,
      evidenceVideos: evidence.videos,
    };
  }

  const inferred = inferReturnFromTimeline(row, sub);
  if (!inferred) return null;
  const evidence = mapReturnEvidence(
    inferred.evidence_images,
    inferred.evidence_videos
  );
  return {
    status: inferred.status,
    reason: inferred.reason,
    customerNote: inferred.customer_note,
    vendorNote: inferred.vendor_note,
    decidedBy: "",
    requestedAt: inferred.requested_at,
    resolvedAt: inferred.resolved_at,
    evidenceImages: evidence.images,
    evidenceVideos: evidence.videos,
  };
}

function formatPaymentLabel(value) {
  const s = String(value || "").trim();
  if (!s) return "—";
  return s
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function mapAvailableActions(source) {
  const a =
    source?.available_actions && typeof source.available_actions === "object"
      ? source.available_actions
      : source?.availableActions && typeof source.availableActions === "object"
        ? source.availableActions
        : null;
  if (!a) return null;
  return {
    canAccept: Boolean(a.can_accept ?? a.canAccept),
    canReject: Boolean(a.can_reject ?? a.canReject),
    canCancel: Boolean(a.can_cancel ?? a.canCancel),
    canPack: Boolean(a.can_pack ?? a.canPack),
    canDispatch: Boolean(a.can_dispatch ?? a.canDispatch),
    canUpdateStatus: Boolean(a.can_update_status ?? a.canUpdateStatus),
    canApproveReturn: Boolean(a.can_approve_return ?? a.canApproveReturn),
    canRejectReturn: Boolean(a.can_reject_return ?? a.canRejectReturn),
    canReplaceReturn: Boolean(a.can_replace_return ?? a.canReplaceReturn),
    canUpdateReturn: Boolean(a.can_update_return ?? a.canUpdateReturn),
    canCompleteReturn: Boolean(a.can_complete_return ?? a.canCompleteReturn),
    canInitiateRefund: Boolean(
      a.can_initiate_refund ?? a.canInitiateRefund
    ),
  };
}

export function isVendorShopOrderPendingAction(order, statusGroup = "") {
  if (!order) return false;
  const actions = order.availableActions;
  if (actions && typeof actions === "object") {
    return Boolean(actions.canAccept || actions.canReject);
  }
  if (String(statusGroup || "").toLowerCase() === "pending") return true;
  const raw = order.raw && typeof order.raw === "object" ? order.raw : {};
  const sub = resolveVendorSuborder(raw) || raw;
  const status = String(sub.order_status || raw.vendor_order_status || raw.order_status || "")
    .toLowerCase()
    .trim();
  const label = String(sub.status_label || order.orderStatus || "")
    .toLowerCase()
    .trim();
  if (
    status === "pending" ||
    status === "new" ||
    status === "awaiting_vendor" ||
    status === "awaiting_vendor_acceptance"
  ) {
    return true;
  }
  return label.includes("new") || label === "pending" || label.includes("awaiting");
}

export function orderRowShowsAcceptReject(row, statusGroup = "") {
  const actions = row?.availableActions;
  if (actions && typeof actions === "object") {
    return Boolean(actions.canAccept || actions.canReject);
  }
  return String(statusGroup || "").toLowerCase() === "pending";
}

export const SHOP_ORDER_STATUS_GROUP = {
  "New Orders": "pending",
  "Ongoing Orders": "ongoing",
  "Completed Orders": "delivered",
  "Cancelled Orders": "cancelled",
  "Rejected Orders": "rejected",
  "Return Order": "returns",
};

export function orderRowShowsReturnActions(row, statusGroup = "") {
  const group = String(statusGroup || "").toLowerCase();
  if (group === "cancelled" || group === "rejected") return false;
  const a = row?.availableActions;
  if (a && typeof a === "object") {
    return Boolean(a.canRejectReturn || a.canCompleteReturn);
  }
  return group === "returns";
}

export function formatVendorRefundStatusLabel(refundStatus, { returnsContext = false } = {}) {
  const key = String(refundStatus || "")
    .trim()
    .toLowerCase();
  if (!key || key === "none") {
    return returnsContext ? "Pending" : "—";
  }
  return key.replace(/_/g, " ");
}

export function formatVendorReturnStatusLabel(returnStatus) {
  const key = String(returnStatus || "")
    .trim()
    .toLowerCase();
  if (!key || key === "none") return "—";
  const labels = {
    rejected: "Rejected",
    return_rejected: "Return rejected",
    requested: "Requested",
    return_requested: "Return requested",
    approved: "Approved",
    return_approved: "Return approved",
    processing: "Processing",
    completed: "Completed",
  };
  return labels[key] || key.replace(/_/g, " ");
}

function normalizeStatusKey(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
}

export function resolveVendorSuborderStatusKey(row) {
  const raw = row?.raw && typeof row.raw === "object" ? row.raw : row;
  const sub = resolveVendorSuborder(raw) || raw;
  return normalizeStatusKey(
    sub?.order_status || row?.vendorOrderStatus || row?.orderStatus || ""
  );
}

export function orderRowShowsMarkReadyToShip(row, statusGroup = "") {
  if (String(statusGroup || "").toLowerCase() !== "ongoing") return false;
  const a = row?.availableActions;
  if (a && typeof a === "object" && a.canPack != null) {
    return Boolean(a.canPack);
  }
  const key = resolveVendorSuborderStatusKey(row);
  return ["processing", "awaiting_vendor_acceptance"].includes(key);
}

export function orderRowShowsMarkShipped(row, statusGroup = "") {
  if (String(statusGroup || "").toLowerCase() !== "ongoing") return false;
  const a = row?.availableActions;
  if (a && typeof a === "object" && a.canDispatch != null) {
    return Boolean(a.canDispatch);
  }
  return resolveVendorSuborderStatusKey(row) === "ready_to_ship";
}

/** @deprecated use orderRowShowsMarkReadyToShip / orderRowShowsMarkShipped */
export function orderRowShowsDispatch(row, statusGroup = "") {
  return (
    orderRowShowsMarkReadyToShip(row, statusGroup) ||
    orderRowShowsMarkShipped(row, statusGroup)
  );
}

/** PUT suborder/status body for the next forward step (ready_to_ship or shipped). */
export function nextVendorSuborderStatusUpdate(row) {
  const a = row?.availableActions;
  if (a?.canDispatch) {
    return {
      orderStatus: "shipped",
      actionLabel: "Mark shipped",
      successMessage: "Order marked shipped",
    };
  }
  if (a?.canPack) {
    return {
      orderStatus: "ready_to_ship",
      actionLabel: "Mark ready to ship",
      successMessage: "Order marked ready to ship",
    };
  }
  const key = resolveVendorSuborderStatusKey(row);
  if (key === "ready_to_ship") {
    return {
      orderStatus: "shipped",
      actionLabel: "Mark shipped",
      successMessage: "Order marked shipped",
    };
  }
  if (["processing", "awaiting_vendor_acceptance"].includes(key)) {
    return {
      orderStatus: "ready_to_ship",
      actionLabel: "Mark ready to ship",
      successMessage: "Order marked ready to ship",
    };
  }
  return null;
}

export function orderRowShowsRefundInitiate(row) {
  return Boolean(row?.availableActions?.canInitiateRefund);
}

const RETURN_MODAL_ACTIONS = [
  { id: "approved", label: "Approve return" },
  { id: "rejected", label: "Reject return" },
  { id: "completed", label: "Mark return completed" },
];

/** Options for POST return/status based on return_request.status + available_actions. */
export function getVendorReturnModalActions(order) {
  const a = order?.availableActions;
  const st = normalizeStatusKey(
    order?.returnStatus ||
      order?.returnRequest?.status ||
      order?.returnRequest?.return_status
  );
  if (!a || typeof a !== "object") {
    if (st === "requested") {
      return RETURN_MODAL_ACTIONS.filter((x) => x.id !== "completed");
    }
    if (["approved", "processing"].includes(st)) {
      return RETURN_MODAL_ACTIONS.filter((x) => x.id === "completed");
    }
    return RETURN_MODAL_ACTIONS;
  }
  return RETURN_MODAL_ACTIONS.filter((opt) => {
    if (opt.id === "approved") return st === "requested";
    if (opt.id === "rejected") return Boolean(a.canRejectReturn);
    if (opt.id === "completed") return Boolean(a.canCompleteReturn);
    return false;
  });
}

function toMoney(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function formatMoney(value) {
  return toMoney(value, 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  return `${day}/${month}/${year} - ${String(hours).padStart(2, "0")}:${minutes} ${ampm}`;
}

function refName(value) {
  if (value == null || value === "") return "";
  if (typeof value === "string" || typeof value === "number") return String(value).trim();
  if (typeof value !== "object") return "";
  return String(
    value.full_name ??
      value.name ??
      value.customer_name ??
      value.title ??
      ""
  ).trim();
}

function resolveVendorCustomer(row) {
  if (!row || typeof row !== "object") {
    return { name: "", email: "", phone: "" };
  }
  const nested =
    (row.user && typeof row.user === "object" ? row.user : null) ||
    (row.customer && typeof row.customer === "object" ? row.customer : null) ||
    (row.user_id && typeof row.user_id === "object" ? row.user_id : null) ||
    {};
  const name =
    refName(nested) ||
    String(row.customer_name || nested.customer_name || "").trim();
  const email = String(
    nested.email ||
      nested.customer_email ||
      row.customer_email ||
      row.email ||
      ""
  ).trim();
  const phone = String(
    nested.phone ||
      nested.mobile ||
      nested.customer_phone ||
      row.customer_phone ||
      row.customer_mobile ||
      ""
  ).trim();
  return { name, email, phone };
}

function extractList(data) {
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== "object") return [];
  if (Array.isArray(data.orders)) return data.orders;
  if (Array.isArray(data.list)) return data.list;
  if (Array.isArray(data.docs)) return data.docs;
  if (Array.isArray(data.results)) return data.results;
  if (Array.isArray(data.vendor_suborders)) return data.vendor_suborders;
  if (Array.isArray(data.data)) return data.data;
  return [];
}

function extractPagination(data, page, limit, count) {
  const src =
    data?.pagination && typeof data.pagination === "object"
      ? data.pagination
      : data || {};
  const nextLimit = Number(src.limit) || limit;
  const total = Number(src.total ?? src.totalItems ?? src.count) || count;
  const totalPages =
    Number(src.totalPages ?? src.total_pages) ||
    Math.max(1, Math.ceil(total / nextLimit) || 1);
  return {
    page: Number(src.currentPage ?? src.page ?? src.current_page) || page,
    limit: nextLimit,
    total,
    totalPages,
  };
}

function mapItem(item, index = 0) {
  if (!item || typeof item !== "object") return null;
  const product =
    item.product_id && typeof item.product_id === "object" ? item.product_id : item;
  const productId = String(
    (typeof item.product_id === "object" && item.product_id?._id) ||
      item.product_id ||
      product._id ||
      ""
  ).trim();
  const sizeLabel = String(item.size || item.size_label || "").trim();
  const colorLabel = String(item.color || item.color_label || "").trim();
  return {
    id: productId || item._id || item.id || index + 1,
    productId,
    name: String(item.title || product.title || product.name || "Product").trim(),
    title: String(item.title || product.title || product.name || "Product").trim(),
    quantity: toMoney(item.quantity, 1),
    size: sizeLabel || "—",
    color: colorLabel || "—",
    sizeId: String(item.size_id ?? "").trim(),
    colorId: String(item.color_id ?? "").trim(),
    price: formatMoney(item.total_item_price ?? item.unit_price ?? item.mrp),
    unitPrice: formatMoney(item.unit_price ?? item.mrp),
    mrp: toMoney(item.mrp, 0),
    totalItemPrice: toMoney(item.total_item_price, 0),
    discount: String(item.discount ?? "—").trim() || "—",
    sku: item.sku == null || item.sku === "" ? "—" : String(item.sku),
    category: String(item.category || product.category || "—").trim() || "—",
    brand: String(item.brand_name || item.brand || product.brand || "—").trim() || "—",
    description: String(item.description || product.description || "").trim(),
    image:
      item.cover_image ||
      item.image ||
      product.cover_image ||
      product.image ||
      "",
    weight: item.weight,
  };
}

export function mapVendorShopOrder(row, index = 0) {
  if (!row || typeof row !== "object") return null;
  const sub = resolveVendorSuborder(row);
  const subOrRow = sub || row;
  const address =
    row.address && typeof row.address === "object"
      ? row.address
      : row.customer_address && typeof row.customer_address === "object"
        ? row.customer_address
        : {};
  const buyer = resolveVendorCustomer(row);
  const breakdown =
    row.vendor_price_breakdown && typeof row.vendor_price_breakdown === "object"
      ? row.vendor_price_breakdown
      : row.price_breakdown && typeof row.price_breakdown === "object"
        ? row.price_breakdown
        : subOrRow.vendor_price_breakdown && typeof subOrRow.vendor_price_breakdown === "object"
          ? subOrRow.vendor_price_breakdown
          : {};
  const itemsSource = Array.isArray(subOrRow.items)
    ? subOrRow.items
    : Array.isArray(row.items)
      ? row.items
      : [];
  const shopOrderId = String(
    row.shop_order_id || row._id || row.id || subOrRow.master_order_id || ""
  ).trim();
  const orderId = String(
    row.order_number ||
      subOrRow.master_order_number ||
      subOrRow.suborder_number ||
      shopOrderId ||
      index + 1
  ).trim();
  const suborderNumber = String(subOrRow.suborder_number || "").trim();
  const mrp = toMoney(
    breakdown.total_mrp ?? row.net_price_amount ?? row.vendor_item_total ?? subOrRow.vendor_item_total,
    0
  );
  const payable = toMoney(
    row.vendor_item_total ??
      breakdown.vendor_item_total ??
      breakdown.item_subtotal ??
      row.final_amount ??
      row.net_amount ??
      subOrRow.vendor_item_total,
    mrp
  );
  const availableActions =
    mapAvailableActions(subOrRow) ||
    mapAvailableActions(row) ||
    mapAvailableActions(row.vendor_fulfillment);
  const addressLine = [
    address.house_no,
    address.street,
    address.address_line1,
    address.address_line2,
    address.landmark,
  ]
    .filter((p) => p != null && String(p).trim() !== "")
    .join(", ");

  let returnRequest = resolveVendorReturnRequest(row);
  if (!returnRequest) {
    const subKey = normalizeStatusKey(
      subOrRow.order_status || row.vendor_order_status || row.order_status
    );
    if (subKey.includes("return")) {
      returnRequest = inferReturnFromTimeline(row, subOrRow);
    }
  }
  const returnStatus =
    returnRequest?.status ||
    (normalizeStatusKey(subOrRow.order_status || row.order_status) ===
    "return_rejected"
      ? "rejected"
      : "");

  return {
    id: shopOrderId || orderId,
    shopOrderId,
    orderId,
    suborderNumber,
    customerName:
      buyer.name ||
      address.full_name ||
      String(row.customer_name || "").trim() ||
      "—",
    customerMobile: String(
      buyer.phone || address.phone || row.customer_mobile || "—"
    ).trim(),
    city: String(address.city || row.city || "—").trim(),
    state: String(address.state || row.state || "—").trim(),
    pincode: String(address.pincode || row.pincode || "—").trim(),
    addressLine: addressLine || "—",
    email: buyer.email || "—",
    orderDateAndTime: formatDateTime(
      row.createdAt || row.created_at || row.request_date || subOrRow.accepted_at
    ),
    paymentType: formatPaymentLabel(
      row.payment_method ||
        row.payment_type ||
        row.payment_status ||
        subOrRow.payment_status
    ),
    paymentStatus: String(row.payment_status || "").trim(),
    netPriceAmount: formatMoney(mrp),
    netAmount: formatMoney(payable),
    paidAmount: formatMoney(row.paid_amount),
    couponCode: String(row.coupon_code || "—").trim() || "—",
    orderStatus: String(
      subOrRow.status_label ||
        row.status_label ||
        row.vendor_order_status ||
        subOrRow.order_status ||
        row.order_status ||
        "—"
    ).trim(),
    vendorOrderStatus: String(
      subOrRow.order_status || row.vendor_order_status || ""
    ).trim(),
    suborderStatusKey: normalizeStatusKey(subOrRow.order_status || row.vendor_order_status),
    statusActionLabel: String(
      subOrRow.status_action_label ||
        row.vendor_fulfillment?.status_action_label ||
        ""
    ).trim(),
    masterOrderStatusLabel: String(
      subOrRow.master_order_status_label || row.master_order_status_label || ""
    ).trim(),
    cancelledActionLabel: String(
      subOrRow.cancelled_action_label ||
        row.vendor_fulfillment?.cancelled_action_label ||
        ""
    ).trim(),
    refundActionLabel: String(
      subOrRow.refund_action_label ||
        row.vendor_fulfillment?.refund_action_label ||
        ""
    ).trim(),
    availableActions,
    returnRequest,
    returnStatus: String(returnStatus || "").trim(),
    refundStatus: String(
      subOrRow.refund_status || row.refund_status || ""
    ).trim(),
    refundError: String(subOrRow.refund_error || row.refund_error || "").trim(),
    refundAmount: toMoney(subOrRow.refund_amount ?? row.refund_amount, 0),
    items: itemsSource.map(mapItem).filter(Boolean),
    raw: row,
  };
}

export async function fetchVendorShopOrders({
  statusGroup = "pending",
  page = 1,
  limit = 10,
  search = "",
} = {}) {
  const response = await handleApiCall(
    "get",
    endpoint.vendorShopOrders({ statusGroup, page, limit, search })
  );
  const body = response?.data ?? {};
  const ok = Number(body.code) === 200 || body.success === true;
  const data = body.data ?? body;
  if (!ok) {
    return {
      ok: false,
      orders: [],
      pagination: { page, limit, total: 0, totalPages: 1 },
      message: String(body.message || "Could not load shop orders"),
    };
  }
  const rows = extractList(data);
  const orders = rows.map(mapVendorShopOrder).filter(Boolean);
  return {
    ok: true,
    orders,
    pagination: extractPagination(data, page, limit, orders.length),
    message: "",
  };
}

export async function fetchVendorShopOrderDetail(shopOrderId) {
  const id = String(shopOrderId || "").trim();
  if (!id) {
    return { ok: false, order: null, message: "Order missing" };
  }
  const response = await handleApiCall("get", endpoint.vendorShopOrder(id));
  const body = response?.data ?? {};
  const ok = Number(body.code) === 200 || body.success === true;
  const data = body.data ?? body;
  const raw =
    data?.order && typeof data.order === "object"
      ? data.order
      : data?.shop_order && typeof data.shop_order === "object"
        ? data.shop_order
        : Array.isArray(data?.orders) && data.orders[0]
          ? data.orders[0]
          : data && typeof data === "object"
            ? data
            : null;
  const order = mapVendorShopOrder(raw);
  if (!ok || !order) {
    return {
      ok: false,
      order: null,
      message: String(body.message || "Could not load order"),
    };
  }
  return { ok: true, order, message: "" };
}

export async function acceptVendorShopSuborder(shopOrderId) {
  const id = String(shopOrderId || "").trim();
  if (!id) return { ok: false, message: "Order missing" };
  const response = await handleApiCall(
    "post",
    endpoint.vendorShopSuborderAccept(id),
    {}
  );
  const body = responseBody(response);
  const ok =
    !response?.isAxiosError &&
    (response?.status === 200 ||
      response?.status === 201 ||
      isApiOk(body));
  return {
    ok,
    message: ok
      ? apiMessage(body, "Order accepted")
      : apiMessage(body, "Could not accept order"),
  };
}

export async function rejectVendorShopSuborder(
  shopOrderId,
  { reason = "", reasonNote = "" } = {}
) {
  const id = String(shopOrderId || "").trim();
  const nextReason = String(reason || "").trim();
  const reason_note = String(reasonNote || "").trim();
  if (!id) return { ok: false, message: "Order missing" };
  if (!nextReason) return { ok: false, message: "Select a rejection reason" };
  const response = await handleApiCall(
    "post",
    endpoint.vendorShopSuborderReject(id),
    { reason: nextReason, reason_note }
  );
  const body = responseBody(response);
  const ok =
    !response?.isAxiosError &&
    (response?.status === 200 ||
      response?.status === 201 ||
      isApiOk(body));
  return {
    ok,
    message: ok
      ? apiMessage(body, "Order rejected")
      : apiMessage(body, "Could not reject order"),
  };
}

async function postReturnAction(shopOrderId, url, data = {}) {
  const id = String(shopOrderId || "").trim();
  if (!id) return { ok: false, message: "Order missing" };
  const response = await handleApiCall("post", url, data);
  const body = responseBody(response);
  const ok =
    !response?.isAxiosError &&
    (response?.status === 200 ||
      response?.status === 201 ||
      isApiOk(body));
  return {
    ok,
    message: ok
      ? apiMessage(body, "Return updated")
      : apiMessage(body, "Could not update return"),
  };
}

export async function updateVendorShopSuborderStatus(
  shopOrderId,
  { orderStatus = "ready_to_ship" } = {}
) {
  const id = String(shopOrderId || "").trim();
  const order_status = String(orderStatus || "").trim();
  if (!id) return { ok: false, message: "Order missing" };
  if (!order_status) return { ok: false, message: "Status missing" };
  const response = await handleApiCall(
    "put",
    endpoint.vendorShopSuborderStatus(id),
    { order_status }
  );
  const body = responseBody(response);
  const ok =
    !response?.isAxiosError &&
    (response?.status === 200 ||
      response?.status === 201 ||
      isApiOk(body));
  const defaultSuccess =
    order_status === "shipped"
      ? "Order marked shipped"
      : "Order marked ready to ship";
  return {
    ok,
    message: ok
      ? apiMessage(body, defaultSuccess)
      : apiMessage(body, "Could not update order status"),
  };
}

export async function updateVendorShopReturnStatus(
  shopOrderId,
  { status = "", vendorNote = "" } = {}
) {
  const id = String(shopOrderId || "").trim();
  const nextStatus = String(status || "").trim().toLowerCase();
  if (!id) return { ok: false, message: "Order missing" };
  if (!nextStatus) return { ok: false, message: "Select return status" };
  const payload = { status: nextStatus };
  const vendor_note = String(vendorNote || "").trim();
  if (vendor_note) payload.vendor_note = vendor_note;
  return postReturnAction(
    shopOrderId,
    endpoint.vendorShopReturnStatus(id),
    payload
  );
}

export async function initiateVendorShopRefund(shopOrderId) {
  const id = String(shopOrderId || "").trim();
  if (!id) return { ok: false, message: "Order missing" };
  const response = await handleApiCall(
    "post",
    endpoint.vendorShopSuborderRefundInitiate(id),
    {}
  );
  const body = responseBody(response);
  const ok =
    !response?.isAxiosError &&
    (response?.status === 200 ||
      response?.status === 201 ||
      isApiOk(body));
  return {
    ok,
    message: ok
      ? apiMessage(body, "Refund initiated")
      : apiMessage(body, "Could not initiate refund"),
  };
}

export async function approveVendorShopReturn(shopOrderId) {
  return updateVendorShopReturnStatus(shopOrderId, { status: "approved" });
}

export async function rejectVendorShopReturn(shopOrderId, { vendorNote = "" } = {}) {
  const vendor_note = String(vendorNote || "").trim();
  if (!vendor_note) {
    return { ok: false, message: "Enter a note for the customer" };
  }
  return updateVendorShopReturnStatus(shopOrderId, {
    status: "rejected",
    vendorNote: vendor_note,
  });
}

export async function replaceVendorShopReturn(
  shopOrderId,
  { vendorNote = "", replacementOrderNumber = "" } = {}
) {
  const vendor_note = String(vendorNote || "").trim();
  const replacement_order_number = String(replacementOrderNumber || "").trim();
  if (!vendor_note) {
    return { ok: false, message: "Enter a note for the customer" };
  }
  if (!replacement_order_number) {
    return { ok: false, message: "Enter replacement order number" };
  }
  return postReturnAction(
    shopOrderId,
    endpoint.vendorShopReturnReplace(shopOrderId),
    { vendor_note, replacement_order_number }
  );
}
